import type {
  AttentionItem,
  CaseSummary,
  CurrentUser,
  DashboardSummary,
  QuoteSummary,
} from '@/types/domain'

/* Fixtures for working on the UI with no server at all.
 *
 * Figures are taken verbatim from the UX mockups so a rendered screen can be
 * diffed against `ASA Gateway UI mockups/Dashboard.dc.html` directly. These are
 * NOT seed data and must never become database rows — the real Master File
 * import is a separate, validated path (AD-16).
 *
 * The API is up and serves this same dataset (backend/internal/showcase), so
 * the real client path is the default and mocks are opt-in: set
 * `VITE_USE_MOCKS=true` for offline UI work. The two datasets are kept in step
 * by hand until each showcase route is replaced by its real module, at which
 * point the corresponding fixture here loses its reason to exist. */

export const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === 'true'

export const MOCK_SESSION: CurrentUser = {
  id: 'u-1',
  name: 'Arslan Kerimov',
  email: 'arslan@asagateway.com',
  role: 'super-admin',
  grants: { manageClients: true, manageParticipants: true, manageTransport: true },
  // The mock founder holds the superuser flag, so `can()` short-circuits and
  // the enumerated list stays empty — exactly as the server serves it. A mock
  // that listed every capability would pass checks the real payload passes for
  // a different reason, and would drift the moment a capability was added.
  capabilities: [],
  isSuperuser: true,
  language: 'en',
}

export const MOCK_DASHBOARD: DashboardSummary = {
  counters: {
    activeCases: 41,
    shipmentsThisMonth: 17,
    shipmentsDelta: 4,
    /* Real as of Story 4.6, so the fixture carries a figure rather than leaving
       the tile to render nothing offline. A LOSS, deliberately: the mock's job
       is to exercise the shapes a live screen has to survive, and a negative
       forecast is the one that drives the FR-52 minus row below — a fixture
       that only ever shows profit is a fixture nobody notices the sign code
       under. */
    forecastProfit: { amount: '-4820.00', currency: 'USD' },
  },

  /* Only the `stuck` kind, because only the stuck kind is computable. The
     minus, approval, overdue and quote-followup rows this fixture used to carry
     were the mockup's, and they needed a forecast profit, a rate approval and
     an invoice — none of which exist yet (Stories 4.6, 4.7, 5.4). A fixture
     richer than the server is how the mock stops being a preview of the
     product and starts being a different product. */
  attention: [
    {
      id: 'stuck-ab-27',
      kind: 'stuck',
      recordKind: 'case',
      recordNumber: 'AB-27',
      title: '4',
      detail: 'Nurana Trading',
      href: '/cases/ab-27',
    },
    {
      id: 'stuck-mg-19',
      kind: 'stuck',
      recordKind: 'case',
      recordNumber: 'MG-19',
      title: '6',
      detail: 'Beg Logistics',
      href: '/cases/mg-19',
    },
  ],

  stuckDays: 3,

  /* A fixed instant, not new Date(). A fixture that stamps itself "now" would
     make the staleness indicator untestable — it could never be seen to go
     stale, which is the one thing it exists to show. */
  readAt: '2026-08-05T08:42:00Z',

  /* The same four the server withholds, for the same reasons. Keeping this in
     step with readmodel.go's pendingFigures is what makes offline UI work
     represent the real screen. */
  pending: [
    /* ONE entry, and it must stay in step with readmodel.go's pendingFigures
       (`turnover`, blocked on 5.5 — billed revenue at booked values, and
       invoice_status admits only 'draft'). The three that used to be here —
       forecastProfit, toCollect, toPay — were declared pending against stories
       that had already shipped, so this fixture went on rendering three tiles as
       unavailable after the server started computing them, and the offline
       screen stopped representing the real one. That is the failure this comment
       exists to prevent: a stale entry here is invisible, because a tile that
       says "not yet" looks deliberate. */
    { key: 'turnover', story: '5.5' },
  ],
}

export const MOCK_CASES: CaseSummary[] = [
  {
    id: 'ab-27',
    number: 'AB-27',
    clientName: 'Nurana Trading',
    clientId: 'cl-nurana',
    status: 'stuck',
    origin: 'Guangzhou',
    destination: 'Ashgabat',
    mode: 'rail',
    etd: '2026-07-12',
    eta: '2026-08-04',
    rate: { amount: '34500.00', currency: 'USD' },
    forecastProfit: { amount: '4820.00', currency: 'USD' },
    daysStalled: 4,
    ownerName: 'Merjen Atayeva',
    clientContactName: 'Maksat Hojayev',
    clientContactEmail: 'maksat@nurana.tm',
  },
  {
    id: 'mg-19',
    number: 'MG-19',
    clientName: 'Beg Logistics',
    clientId: 'cl-beg',
    status: 'in-progress',
    origin: 'Ningbo',
    destination: 'Türkmenbaşy',
    mode: 'sea',
    etd: '2026-07-18',
    eta: '2026-08-12',
    rate: { amount: '28900.00', currency: 'USD' },
    forecastProfit: { amount: '3110.00', currency: 'USD' },
    ownerName: 'Sun Di',
    clientContactName: 'Begench Amanov',
    clientContactEmail: 'beg@beglogistics.tm',
  },
  {
    id: 'ab-11',
    number: 'AB-11',
    clientName: 'Nurana Trading',
    clientId: 'cl-nurana',
    status: 'in-progress',
    origin: 'Yiwu',
    destination: 'Mary',
    mode: 'road',
    etd: '2026-07-02',
    eta: '2026-07-29',
    rate: { amount: '16400.00', currency: 'USD' },
    forecastProfit: { amount: '-820.00', currency: 'USD' },
    ownerName: 'Merjen Atayeva',
    clientContactName: 'Maksat Hojayev',
    clientContactEmail: 'maksat@nurana.tm',
  },
  {
    id: 'ab-24',
    number: 'AB-24',
    clientName: 'Altyn Asyr Import',
    clientId: 'cl-altyn',
    status: 'delivered',
    origin: 'Shanghai',
    destination: 'Ashgabat',
    mode: 'multimodal',
    etd: '2026-06-20',
    eta: '2026-07-21',
    rate: { amount: '51200.00', currency: 'USD' },
    forecastProfit: { amount: '7640.00', currency: 'USD' },
    ownerName: 'Aygul Nazarova',
    clientContactName: 'Gurban Saparov',
    clientContactEmail: 'gurban@altynasyr.tm',
  },
  {
    id: 'mg-17',
    number: 'MG-17',
    clientName: 'Caspian Steel',
    clientId: 'cl-caspian',
    status: 'financially-closed',
    origin: 'Qingdao',
    destination: 'Türkmenabat',
    mode: 'rail',
    etd: '2026-05-28',
    eta: '2026-06-24',
    rate: { amount: '42800.00', currency: 'USD' },
    forecastProfit: { amount: '6180.00', currency: 'USD' },
    ownerName: 'Sun Di',
  },
]

export const MOCK_QUOTES: QuoteSummary[] = [
  {
    id: 'mg-q-14',
    clientId: 'cl-beg',
    number: 'MG-Q-14',
    clientName: 'Beg Logistics',
    // The header's one-line description, which the list column and the board
    // card both read. Absent on AB-Q-32 below, so the empty cell is exercised.
    cargoDescription: 'Ceramic tiles and grout, palletised',
    status: 'sent',
    origin: 'Yiwu',
    destination: 'Ashgabat',
    mode: 'rail',
    sell: { amount: '31200.00', currency: 'USD' },
    margin: { amount: '4180.00', currency: 'USD' },
    marginPercent: '13.4',
    validUntil: '2026-07-31',
    // Awaiting the client, so an offer has issued and the card cites it.
    revisionLabel: 'A',
    ownerName: 'Sun Di',
    // Complete, so the card flags only the client's silence.
    cargo: { positions: 2, packages: 2, packageType: "40'HC", blocked: 0 },
  },
  {
    id: 'ab-q-32',
    clientId: 'cl-altyn',
    number: 'AB-Q-32',
    clientName: 'Altyn Asyr Import',
    status: 'draft',
    origin: 'Ningbo',
    destination: 'Mary',
    mode: 'sea',
    sell: { amount: '22600.00', currency: 'USD' },
    margin: { amount: '2940.00', currency: 'USD' },
    marginPercent: '13.0',
    ownerName: 'Merjen Atayeva',
    // One position short of sendable — the amber flag the board is for.
    cargo: { positions: 1, packages: 4, packageType: 'Pallet', blocked: 1 },
  },
  {
    id: 'ab-q-31',
    clientId: 'cl-caspian',
    number: 'AB-Q-31',
    clientName: 'Caspian Steel',
    cargoDescription: 'Hot-rolled steel coil, mixed gauges',
    status: 'converted',
    origin: 'Guangzhou',
    destination: 'Türkmenbaşy',
    mode: 'multimodal',
    sell: { amount: '47500.00', currency: 'USD' },
    margin: { amount: '6820.00', currency: 'USD' },
    marginPercent: '14.4',
    validUntil: '2026-08-06',
    // Accepted after one round of bargaining: the client holds Rev B.
    revisionLabel: 'B',
    ownerName: 'Merjen Atayeva',
    // Mixed packing: the card counts positions rather than naming one type.
    cargo: { positions: 3, packages: 9, blocked: 0 },
  },
]

export const MOCK_ATTENTION: AttentionItem[] = MOCK_DASHBOARD.attention

/* MOCK_ACTIVITY is gone with the activity feed itself: no table records who did
   what, so the entries could only be invented (Epic 8). */
